import React from 'react';
import { PlusIcon } from 'lucide-react';
import type { CaseEntry } from '../../../types/caseEntry';
import type { QuoteLine } from '../../../types/quote';
import { formatHours, signedHours, diffTone } from '../../../utils/caseEstimate';
import { formatKr, lineSubtotal } from '../../../utils/quote';
import { EntryRow } from './EntryRow';
interface HoursSectionProps {
  estimateLines: QuoteLine[];
  entries: CaseEntry[];
  editingId: string | null;
  onEdit: (entry: CaseEntry) => void;
  onDelete: (entry: CaseEntry) => void;
  onAdd: () => void;
}
export function HoursSection({
  estimateLines,
  entries,
  editingId,
  onEdit,
  onDelete,
  onAdd
}: HoursSectionProps) {
  const estimated = estimateLines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  const registered = entries.reduce((sum, entry) => sum + (entry.quantity || 0), 0);
  const hasEstimate = estimateLines.length > 0;
  return <div data-id="element-231" data-mp="35jaw5w4tk90y">
      <div className="flex items-center justify-between gap-3" data-id="element-232" data-mp="35k41cso4qqgz">
        <h3 className="font-display text-base font-bold tracking-tight text-ink" data-id="element-233" data-mp="35lf9f4olykdw">Timer</h3>
        <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 text-sm font-medium text-accent transition-colors duration-150 hover:text-accent-deep" data-id="element-234" data-mp="nf59r8khzt31">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-235" data-mp="sgchj7mn4s9p" />
          Registrér timer
        </button>
      </div>

      {hasEstimate && <div className="mt-3" data-id="element-236" data-mp="166pp38vme4yi">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-237" data-mp="169tz4mvrnux3">Estimeret fra tilbud</p>
          <ul className="mt-1.5 divide-y divide-line/70 border-y border-line/70" data-id="element-238" data-mp="11b0jant0brtr">
            {estimateLines.map(line => <li key={line.id} className="flex items-center gap-3 py-2.5 text-[15px]" data-id="element-239" data-mp="1b6amvfz2079n">
                <p className="min-w-0 flex-1 truncate text-ink-soft" data-id="element-240" data-mp="3hx0bwi0id84l">{line.description || 'Arbejde'}</p>
                <p className="hidden whitespace-nowrap text-sm text-ink-muted sm:block" data-id="element-241" data-mp="3cygosea6hqan">
                  {formatHours(line.quantity || 0)} · {formatKr(line.unitPrice)}/time
                </p>
                <p className="w-28 shrink-0 whitespace-nowrap text-right text-ink-soft" data-id="element-242" data-mp="aia3asnxd5z0">{formatKr(lineSubtotal(line))}</p>
              </li>)}
          </ul>
        </div>}

      <div className="mt-4" data-id="element-243" data-mp="kgpvkyyoula3">
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-244" data-mp="kgznis3an2if">Registreret på sagen</p>
        {entries.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-245" data-mp="pfkfk48e6fvs">Ingen timer registreret endnu.</p> : <ul className="mt-1.5 divide-y divide-line/70 border-y border-line/70" data-id="element-246" data-mp="tfbamk4ybi64">
            {entries.map(entry => <EntryRow key={entry.id} entry={entry} active={editingId === entry.id} onEdit={onEdit} onDelete={onDelete} data-id="element-247" data-mp="1377kesgegwq6" />)}
          </ul>}
      </div>

      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-248" data-mp="1d5n2y71d2xo2">
        {hasEstimate && <div className="flex gap-1.5" data-id="element-249" data-mp="2xp0j8parvme4">
            <dt className="text-ink-muted" data-id="element-250" data-mp="2somdjdwn8oj9">Estimeret</dt>
            <dd className="font-medium text-ink" data-id="element-251" data-mp="2sn1htkylezjt">{formatHours(estimated)}</dd>
          </div>}
        <div className="flex gap-1.5" data-id="element-252" data-mp="2npi5wrpdayv1">
          <dt className="text-ink-muted" data-id="element-253" data-mp="2iqezp3xnimlh">Samlet registreret</dt>
          <dd className="font-medium text-ink" data-id="element-254" data-mp="2irzvsxzm6dlg">{formatHours(registered)}</dd>
        </div>
        {hasEstimate && <div className="flex gap-1.5" data-id="element-255" data-mp="3gfruxxi8fovo">
            <dt className="text-ink-muted" data-id="element-256" data-mp="3bfvnfoabtfbd">Forskel</dt>
            <dd className={`font-medium ${diffTone(registered - estimated)}`} data-id="element-257" data-mp="3bh6sdrfict42">{signedHours(registered - estimated)}</dd>
          </div>}
      </dl>
    </div>;
}